import React, { useState } from 'react';
import {
  Alert,
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  PlusOutlined,
  FileAddOutlined,
  ReloadOutlined,
  DeleteOutlined,
  EditOutlined,
  ClearOutlined,
} from '@ant-design/icons';
import { addPppBatch, addStaticIpBatch, clearNetworkConfig } from '../api/server';

// 可编辑字段规格（驱动单条表单与批量表格）
export interface NetConfFieldSpec {
  key: string;
  label: string;
  type: 'text' | 'number' | 'ipv6';
  required?: boolean;
  placeholder?: string;
  width?: number;
}

interface NetworkConfigPanelProps {
  deviceId: string;
  kind: 'ppp' | 'staticIp';
  configs: any[];
  columns: ColumnsType<any>;
  fields: NetConfFieldSpec[];
  idKey: string; // 'pppId' | 'ipId'
  otherCount: number; // 另一类型配置数量（用于互斥清空警告）
  otherLabel: string; // '静态IP' | 'PPP'
  returnUrl?: string;
  readonly?: boolean; // 只读模式（viewer 角色）：隐藏所有写操作入口
  onRefresh: () => void;
}

const NetworkConfigPanel: React.FC<NetworkConfigPanelProps> = ({
  deviceId,
  kind,
  configs,
  columns,
  fields,
  idKey,
  otherCount,
  otherLabel,
  returnUrl,
  readonly = false,
  onRefresh,
}) => {
  const kindLabel = kind === 'ppp' ? 'PPP配置' : '静态IP配置';
  const batchKey = kind === 'ppp' ? 'pppConfigs' : 'ipConfigs';

  const [singleForm] = Form.useForm();
  const [batchForm] = Form.useForm();
  const [singleOpen, setSingleOpen] = useState(false);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [batchOpen, setBatchOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // 现有列表中的下一个可用 ID
  const nextId = (extra: number[] = []) => {
    const ids = [
      ...configs.map((c) => Number(c[idKey]) || 0),
      ...extra.map((n) => Number(n) || 0),
    ];
    return ids.length ? Math.max(...ids) + 1 : 1;
  };

  // 行数据 → 提交 DTO（只保留已填写字段）
  const toDto = (row: any) => {
    const dto: any = {};
    fields.forEach((f) => {
      const v = row[f.key];
      if (v !== undefined && v !== null && v !== '') {
        dto[f.key] = f.type === 'number' ? Number(v) : v;
      }
    });
    return dto;
  };

  // 全量提交（后端会先清空 PPP+静态IP 两表再插入，并 MQTT 下发设备）
  const submit = async (list: any[], submitReturnUrl?: string) => {
    try {
      setSubmitting(true);
      const payload: any = { [batchKey]: list };
      if (submitReturnUrl && submitReturnUrl.trim()) {
        payload.returnUrl = submitReturnUrl.trim();
      }
      if (kind === 'ppp') {
        await addPppBatch(deviceId, payload);
      } else {
        await addStaticIpBatch(deviceId, payload);
      }
      message.success(`已提交 ${list.length} 条${kindLabel}并下发设备`);
      setSingleOpen(false);
      setBatchOpen(false);
      onRefresh();
    } catch (e: any) {
      const msg = e?.response?.data?.message;
      message.error(Array.isArray(msg) ? msg.join('；') : msg || '提交失败');
    } finally {
      setSubmitting(false);
    }
  };

  // 互斥/清空风险提示：存在另一类型配置或提交后本类型为空时需二次确认
  const submitWithGuard = (list: any[], submitReturnUrl?: string) => {
    const warns: string[] = [];
    if (otherCount > 0) {
      warns.push(`设备当前存在 ${otherCount} 条${otherLabel}配置，提交后将被清空（PPPoE 与静态IP模式互斥）`);
    }
    if (list.length === 0) {
      warns.push(`提交后设备将不再保留任何${kindLabel}`);
    }
    if (warns.length === 0) {
      void submit(list, submitReturnUrl);
      return;
    }
    Modal.confirm({
      title: '提交确认',
      content: `${warns.join('；')}。配置提交后将通过 MQTT 实时下发设备执行，是否继续？`,
      okText: '继续提交',
      cancelText: '取消',
      onOk: () => submit(list, submitReturnUrl),
    });
  };

  // ID 重复校验
  const checkDup = (rows: any[], excludeIndex: number | null = null): string | null => {
    const ids = rows.map((r) => Number(r[idKey]));
    if (new Set(ids).size !== ids.length) return '提交列表中存在重复 ID';
    if (
      configs.some(
        (c, i) => i !== excludeIndex && ids.includes(Number(c[idKey]))
      )
    ) {
      return `ID 与现有${kindLabel}冲突`;
    }
    return null;
  };

  // ---------------- 单条添加 / 编辑 ----------------
  const openSingle = (index: number | null) => {
    setEditIndex(index);
    setSingleOpen(true);
    setTimeout(() => {
      if (index === null) {
        const defaults: any = { [idKey]: nextId() };
        if (fields.some((f) => f.key === 'ipv6')) defaults.ipv6 = 1;
        singleForm.setFieldsValue(defaults);
      } else {
        const row = configs[index];
        const values: any = {};
        fields.forEach((f) => {
          values[f.key] = row[f.key] ?? undefined;
        });
        singleForm.setFieldsValue(values);
      }
    }, 0);
  };

  const onSingleFinish = (values: any) => {
    const list = configs.map(toDto);
    if (editIndex === null) {
      const dup = checkDup([...configs, values]);
      if (dup) {
        message.error(dup);
        return;
      }
      list.push(toDto(values));
    } else {
      const merged = configs.map((c, i) => (i === editIndex ? { ...c, ...values } : c));
      const dup = checkDup(merged, editIndex);
      if (dup) {
        message.error(dup);
        return;
      }
      list.splice(editIndex, 1, toDto({ ...configs[editIndex], ...values }));
    }
    submitWithGuard(list, values.returnUrl);
  };

  // ---------------- 删除单条（剩余列表全量提交） ----------------
  const onDelete = (index: number) => {
    const list = configs.filter((_, i) => i !== index).map(toDto);
    submitWithGuard(list);
  };

  // ---------------- 批量添加（追加到现有列表后全量提交） ----------------
  const openBatch = () => {
    setBatchOpen(true);
    setTimeout(() => {
      const row: any = { [idKey]: nextId() };
      if (fields.some((f) => f.key === 'ipv6')) row.ipv6 = 1;
      batchForm.setFieldsValue({ rows: [row], returnUrl: returnUrl || undefined });
    }, 0);
  };

  const onBatchFinish = (values: any) => {
    const rows: any[] = values.rows || [];
    if (rows.length === 0) {
      message.error('请至少填写一行配置');
      return;
    }
    const dup = checkDup([...configs, ...rows]);
    if (dup) {
      message.error(dup);
      return;
    }
    const list = [...configs.map(toDto), ...rows.map(toDto)];
    submitWithGuard(list, values.returnUrl);
  };

  const addBatchRow = () => {
    const rows: any[] = batchForm.getFieldValue('rows') || [];
    const row: any = { [idKey]: nextId(rows.map((r) => r?.[idKey])) };
    if (fields.some((f) => f.key === 'ipv6')) row.ipv6 = 1;
    batchForm.setFieldsValue({ rows: [...rows, row] });
  };

  // ---------------- 清空网络配置 ----------------
  const onClearAll = async () => {
    try {
      setSubmitting(true);
      const { data } = await clearNetworkConfig(deviceId);
      message.success(data.message || '网络配置已清空');
      onRefresh();
    } catch (e: any) {
      message.error(e?.response?.data?.message || '清空失败');
    } finally {
      setSubmitting(false);
    }
  };

  // ---------------- 表单控件渲染 ----------------
  const renderControl = (f: NetConfFieldSpec) => {
    if (f.type === 'number') {
      return (
        <InputNumber style={{ width: '100%' }} min={0} placeholder={f.placeholder} controls={false} />
      );
    }
    if (f.type === 'ipv6') {
      return (
        <Select
          options={[
            { value: 1, label: '开启' },
            { value: 0, label: '关闭' },
          ]}
        />
      );
    }
    return <Input placeholder={f.placeholder} autoComplete="off" />;
  };

  const rulesOf = (f: NetConfFieldSpec) =>
    f.required ? [{ required: true, message: `${f.label}必填` }] : [];

  // 批量弹窗内的可编辑表格列
  const batchColumns: ColumnsType<any> = [
    ...fields.map((f) => ({
      title: f.label,
      dataIndex: f.key,
      width: f.width,
      render: (_: any, field: any) => (
        <Form.Item
          name={[field.name, f.key]}
          rules={rulesOf(f)}
          style={{ marginBottom: 0 }}
        >
          {renderControl(f)}
        </Form.Item>
      ),
    })),
    {
      title: '操作',
      width: 70,
      render: (_: any, field: any) => (
        <Button
          type="link"
          danger
          size="small"
          onClick={() => {
            const rows: any[] = batchForm.getFieldValue('rows') || [];
            if (rows.length <= 1) {
              message.info('至少保留一行');
              return;
            }
            const next = rows.filter((_, i) => i !== field.name);
            batchForm.setFieldsValue({ rows: next });
          }}
        >
          移除
        </Button>
      ),
    },
  ];

  const actionColumn: ColumnsType<any>[number] = {
    title: '操作',
    key: 'action',
    fixed: 'right',
    width: 130,
    render: (_: any, __: any, index: number) => (
      <Space size="small">
        <Button
          type="link"
          size="small"
          icon={<EditOutlined />}
          onClick={() => openSingle(index)}
        >
          编辑
        </Button>
        <Popconfirm
          title={`删除该${kindLabel}？`}
          description="删除后将把剩余列表全量提交并下发设备"
          onConfirm={() => onDelete(index)}
          okText="删除"
          cancelText="取消"
        >
          <Button type="link" size="small" danger icon={<DeleteOutlined />}>
            删除
          </Button>
        </Popconfirm>
      </Space>
    ),
  };

  const tableColumns = readonly ? columns : [...columns, actionColumn];

  const returnUrlItem = (
    <Form.Item
      name="returnUrl"
      label="回传地址（可选）"
      tooltip="填写后提交时同步更新设备的回传地址，留空则不修改"
    >
      <Input placeholder="https://example.com/callback" autoComplete="off" />
    </Form.Item>
  );

  return (
    <div>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="写入为批量全量覆盖语义"
        description="单条添加 / 批量添加 / 编辑 / 删除均会将完整列表提交：后端先清空该设备的 PPP 与静态IP配置后写入，并经 MQTT 实时下发设备执行（PPPoE 与静态IP模式互斥）。"
      />
      <Space style={{ marginBottom: 12 }} wrap>
        {!readonly && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => openSingle(null)}>
            单条添加
          </Button>
        )}
        {!readonly && (
          <Button icon={<FileAddOutlined />} onClick={openBatch}>
            批量添加
          </Button>
        )}
        <Button icon={<ReloadOutlined />} onClick={onRefresh}>
          刷新
        </Button>
        {!readonly && (
          <Popconfirm
            title="清空网络配置？"
            description="将清空平台侧全部 PPP 与静态IP配置及回传地址（仅平台侧，不下发设备）"
            onConfirm={onClearAll}
            okText="清空"
            okButtonProps={{ danger: true }}
            cancelText="取消"
          >
            <Button danger icon={<ClearOutlined />} loading={submitting}>
              清空网络配置
            </Button>
          </Popconfirm>
        )}
      </Space>

      <Table
        columns={tableColumns}
        dataSource={configs}
        rowKey="id"
        pagination={false}
        scroll={{ x: 'max-content' }}
        size="middle"
      />

      {/* 单条添加 / 编辑 */}
      <Modal
        title={editIndex === null ? `单条添加${kindLabel}` : `编辑${kindLabel}`}
        open={singleOpen}
        onCancel={() => setSingleOpen(false)}
        onOk={() => singleForm.submit()}
        confirmLoading={submitting}
        okText="提交并下发"
        cancelText="取消"
        destroyOnClose
      >
        <Form form={singleForm} layout="vertical" onFinish={onSingleFinish} initialValues={{ returnUrl: returnUrl || undefined }}>
          {fields.map((f) => (
            <Form.Item
              key={f.key}
              name={f.key}
              label={f.label}
              rules={rulesOf(f)}
            >
              {renderControl(f)}
            </Form.Item>
          ))}
          {returnUrlItem}
        </Form>
      </Modal>

      {/* 批量添加 */}
      <Modal
        title={`批量添加${kindLabel}（追加到现有 ${configs.length} 条之后）`}
        open={batchOpen}
        onCancel={() => setBatchOpen(false)}
        onOk={() => batchForm.submit()}
        confirmLoading={submitting}
        okText="提交并下发"
        cancelText="取消"
        width={960}
        destroyOnClose
      >
        <Form form={batchForm} layout="vertical" onFinish={onBatchFinish}>
          <Form.List name="rows">
            {(rows) => (
              <>
                <Table
                  columns={batchColumns}
                  dataSource={rows}
                  rowKey="key"
                  pagination={false}
                  scroll={{ x: 'max-content' }}
                  size="small"
                />
                <Button
                  type="dashed"
                  block
                  icon={<PlusOutlined />}
                  style={{ marginTop: 12 }}
                  onClick={addBatchRow}
                >
                  添加一行
                </Button>
              </>
            )}
          </Form.List>
          <div style={{ marginTop: 16 }}>{returnUrlItem}</div>
        </Form>
      </Modal>
    </div>
  );
};

export default NetworkConfigPanel;
